import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetVendorMasterService from '../../services/assetVendorMasterService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  vendorCode: '',
  vendorName: '',
  vendorDescription: '',
  contactName: '',
  email: '',
  phone: '',
  address: {
    line1: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  },
  services: '',
  status: 'Active',
  notes: ''
}

const AssetVendorMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const vendorData = location.state?.vendorData
  const recordId = vendorData?._id || searchParams.get('id')

  const [formData, setFormData] = useState(emptyForm)
  const [statusOptions, setStatusOptions] = useState(
    () => optionsFromDropdown(null, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus)
  )
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        const response = await assetDropdownService.getOptions()
        if (response.success && response.data) {
          setStatusOptions(optionsFromDropdown(response.data.dropdowns, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus))
        }
      } catch (error) {
        console.error('Error loading vendor dropdown options:', error)
      }
    }
    loadDropdownOptions()
  }, [])

  // Fetch next vendor code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchVendorCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetVendorMasterService.getNextVendorCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, vendorCode: response.data.vendorCode || 'AVND0001' }))
          }
        } catch (error) {
          console.error('Error fetching vendor code:', error)
          setFormData(prev => ({ ...prev, vendorCode: 'AVND0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchVendorCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  // Load record data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadVendorData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadVendorData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetVendorMasterService.getVendorById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          vendorCode: record.vendorCode || '',
          vendorName: record.vendorName || '',
          vendorDescription: record.vendorDescription || '',
          contactName: record.contactName || '',
          email: record.email || '',
          phone: record.phone || '',
          address: {
            line1: record.address?.line1 || '',
            city: record.address?.city || '',
            state: record.address?.state || '',
            country: record.address?.country || '',
            postalCode: record.address?.postalCode || ''
          },
          services: (record.services || []).join(', '),
          status: record.status || 'Active',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading asset vendor data:', error)
      toast.error('Failed to load asset vendor data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSave = async () => {
    try {
      if (!formData.vendorName) {
        toast.error('Please fill in Vendor Name')
        return
      }

      setIsLoading(true)

      // Services are captured as a comma separated list
      const submitData = {
        ...formData,
        services: formData.services
          .split(',')
          .map(service => service.trim())
          .filter(Boolean)
      }

      if (isEditMode && recordId) {
        const response = await assetVendorMasterService.updateVendor(recordId, submitData)
        if (response.success) {
          toast.success('Asset vendor updated successfully!')
          navigate('/assets/vendor-master')
        } else {
          toast.error(response.message || 'Failed to update asset vendor')
        }
      } else {
        const response = await assetVendorMasterService.createVendor(submitData)
        if (response.success) {
          toast.success('Asset vendor created successfully!')
          navigate('/assets/vendor-master')
        } else {
          toast.error(response.message || 'Failed to create asset vendor')
        }
      }
    } catch (error) {
      console.error('Error saving asset vendor:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save asset vendor')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadVendorData(recordId)
    } else {
      setFormData({ ...emptyForm, vendorCode: formData.vendorCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Vendors', path: '/assets/vendor-master' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Vendor' : isViewMode ? 'View Vendor' : 'New Vendor'}</h1>
          <p>{isViewMode ? 'View vendor details' : isEditMode ? 'Update vendor information' : 'Create a new asset vendor'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/vendor-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Vendor Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Basic Information</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Vendor Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="vendorCode"
                  value={formData.vendorCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Vendor Name <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="vendorName"
                  value={formData.vendorName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Services (comma separated)</label>
                <input
                  type="text"
                  name="services"
                  value={formData.services}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  placeholder="Hardware Supply, Installation"
                  style={inputStyle}
                />
              </div>

              <div style={fullWidthStyle}>
                <label style={labelStyle}>Description</label>
                <textarea
                  name="vendorDescription"
                  value={formData.vendorDescription}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Contact Details</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Address</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div style={fullWidthStyle}>
                <label style={labelStyle}>Address Line 1</label>
                <input
                  type="text"
                  name="address.line1"
                  value={formData.address.line1}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>City</label>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>State</label>
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Country</label>
                <input
                  type="text"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Postal Code</label>
                <input
                  type="text"
                  name="address.postalCode"
                  value={formData.address.postalCode}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetVendorMasterForm
